import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CloudIcon, ServerIcon, VideoIcon } from "lucide-react";

import { useAlertEvents } from "@/api/alerts";
import {
  useCamera,
  useCameraStatus,
  useDeleteCamera,
  useGeneratePushToken,
} from "@/api/cameras";
import { useStatusFeed } from "@/api/status_feed";
import { CameraFeedPlayer } from "@/components/CameraFeedPlayer";
import { CameraSessionsPanel } from "@/components/CameraSessionsPanel";
import { Badge } from "@/components/ui/badge";
import { statusLabel, connectionLabel } from "@/lib/vocab";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/lib/utils";

function fieldRow(label: string, value: React.ReactNode) {
  return (
    <div className="grid grid-cols-2 gap-2 border-b py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="break-all text-right font-medium">{value ?? "—"}</span>
    </div>
  );
}

export function CameraDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === "admin";

  const { data, isLoading, isError, error } = useCamera(id);
  const { data: status } = useCameraStatus(id);
  const { statuses } = useStatusFeed();
  const del = useDeleteCamera();
  const genToken = useGeneratePushToken();

  const [pushUrl, setPushUrl] = useState<string | null>(null);
  const [pushToken, setPushToken] = useState<string | null>(null);

  if (isLoading) return <p className="py-10 text-center text-sm">Loading…</p>;
  if (isError || !data) {
    return (
      <p className="py-10 text-center text-sm text-destructive">
        Could not load camera: {(error as Error)?.message ?? "not found"}
      </p>
    );
  }

  const liveStatus = statuses[data.id] ?? data.status;
  const isOnline = liveStatus === "online";
  const isDirect = data.connection_mode === "direct";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/cameras"
            className="text-xs text-muted-foreground hover:underline"
          >
            ← All cameras
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            {data.name}
          </h1>
          <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono">{data.registration_code}</span>
            <Badge variant={isOnline ? "online" : "offline"}>{statusLabel(liveStatus)}</Badge>
            <Badge variant={isDirect ? "warning" : "online"}>
              {isDirect ? (
                <CloudIcon className="mr-1 inline h-3 w-3" />
              ) : (
                <ServerIcon className="mr-1 inline h-3 w-3" />
              )}
              {connectionLabel(data.connection_mode)}
            </Badge>
            {status && (
              <span className="text-xs">
                · {status.source} (latency {status.latency_ms ?? "—"} ms)
              </span>
            )}
          </p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              disabled={del.isPending}
              onClick={async () => {
                if (!confirm(`Delete camera ${data.name}?`)) return;
                await del.mutateAsync(data.id);
                nav("/cameras");
              }}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        )}
      </div>

      {/* Live feed — only when an HLS URL is known */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <VideoIcon className="h-4 w-4" />
            Live feed
          </CardTitle>
          {data.hls_url && (
            <code className="truncate text-xs text-muted-foreground">
              {data.hls_url}
            </code>
          )}
        </CardHeader>
        <CardContent>
          <CameraFeedPlayer
            hlsUrl={data.hls_url}
            cameraId={data.id}
            online={isOnline}
            cameraName={data.name}
          />
          {!data.hls_url && (
            <p className="mt-2 text-xs text-muted-foreground">
              No stream is configured for this camera. Set an RTSP URL or
              switch to direct-cloud mode and generate a push token.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Direct-mode push token panel */}
      {isDirect && canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CloudIcon className="h-4 w-4" />
              Direct-cloud push URL
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!pushUrl ? (
              <>
                <p className="text-sm text-muted-foreground">
                  This camera is registered for direct cloud upload. Generate
                  a one-time RTSP push URL to paste into the device's stream
                  configuration. The token is shown only once — save it now.
                </p>
                <Button
                  size="sm"
                  disabled={genToken.isPending}
                  onClick={async () => {
                    const res = await genToken.mutateAsync(data.id);
                    setPushUrl(res.push_url);
                    setPushToken(res.push_token);
                  }}
                >
                  {genToken.isPending
                    ? "Minting…"
                    : "Generate push URL"}
                </Button>
              </>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Save this URL now. It cannot be retrieved later.
                </p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={pushUrl}
                    className="flex-1 rounded border bg-muted/40 px-2 py-1 font-mono text-xs"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      void navigator.clipboard?.writeText(pushUrl);
                    }}
                  >
                    Copy
                  </Button>
                </div>
                {pushToken && (
                  <p className="text-[10px] text-muted-foreground">
                    Token (also embedded above): <span className="font-mono">{pushToken}</span>
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Device</CardTitle>
          </CardHeader>
          <CardContent>
            {fieldRow("Manufacturer", data.manufacturer)}
            {fieldRow("Model", data.model)}
            {fieldRow("Firmware", data.firmware)}
            {fieldRow("Serial", data.serial_number)}
            {fieldRow(
              "Protocol",
              <Badge variant="online">
                {(data as { protocol?: string }).protocol?.toUpperCase() ?? "RTSP"}
              </Badge>,
            )}
            {(data as { onvif_device_uuid?: string | null }).onvif_device_uuid &&
              fieldRow(
                "ONVIF UUID",
                <span className="font-mono text-xs">
                  {(data as { onvif_device_uuid?: string | null }).onvif_device_uuid}
                </span>,
              )}
            {fieldRow(
              "Connection",
              <Badge variant={isDirect ? "warning" : "online"}>
                {connectionLabel(data.connection_mode)}
              </Badge>,
            )}
            {fieldRow(
              "PTZ",
              data.is_ptz ? <Badge variant="online">yes</Badge> : "no",
            )}
            {fieldRow("Audio", data.has_audio ? "yes" : "no")}
            {fieldRow(
              "Resolution",
              data.resolution_width && data.resolution_height
                ? `${data.resolution_width} × ${data.resolution_height}`
                : null,
            )}
            {fieldRow("Framerate", data.framerate)}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Endpoints</CardTitle>
          </CardHeader>
          <CardContent>
            {fieldRow(
              "ONVIF",
              data.onvif_host
                ? `${data.onvif_host}:${data.onvif_port ?? 80}`
                : null,
            )}
            {fieldRow("ONVIF user", data.onvif_username)}
            {fieldRow("ONVIF password", data.onvif_password ?? null)}
            {fieldRow("RTSP main", data.rtsp_main_url)}
            {fieldRow("RTSP sub", data.rtsp_sub_url)}
            {fieldRow("RTSP user", data.rtsp_username)}
            {fieldRow("RTSP password", data.rtsp_password ?? null)}
            {fieldRow("Profile token", data.onvif_profile_token)}
            {fieldRow("HLS URL", data.hls_url)}
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Recent alerts</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <CameraAlertsList cameraId={data.id} />
          </CardContent>
        </Card>

        {/* P1B-FE FE-2 — stream-session observability ("why did it fail"). */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <CameraSessionsPanel cameraId={data.id} />
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Lifecycle</CardTitle>
          </CardHeader>
          <CardContent>
            {fieldRow("Created", formatDate(data.created_at))}
            {fieldRow("Updated", formatDate(data.updated_at))}
            {fieldRow("Last seen", formatDate(data.last_seen_at))}
            {fieldRow(
              "Last status change",
              formatDate(data.last_status_change),
            )}
            {fieldRow("Site ID", data.site_id)}
            {fieldRow("Agent ID", data.agent_id)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


function CameraAlertsList({ cameraId }: { cameraId: string }) {
  const { data } = useAlertEvents({ camera_id: cameraId, limit: 30 });
  if (!data || data.items.length === 0) {
    return (
      <p className="px-4 py-6 text-sm text-muted-foreground">
        No alerts for this camera in the last 30 days.
      </p>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
        <tr>
          <th className="px-4 py-2 text-left">Severity</th>
          <th className="px-4 py-2 text-left">Title</th>
          <th className="px-4 py-2 text-left">Status</th>
          <th className="px-4 py-2 text-left">When</th>
        </tr>
      </thead>
      <tbody>
        {data.items.map((evt) => (
          <tr key={evt.id} className="border-b last:border-0">
            <td className="px-4 py-2">
              <Badge
                variant={
                  evt.severity === "critical"
                    ? "error"
                    : evt.severity === "warning"
                      ? "warning"
                      : "online"
                }
              >
                {evt.severity}
              </Badge>
            </td>
            <td className="px-4 py-2">{evt.title}</td>
            <td className="px-4 py-2">
              <Badge
                variant={
                  evt.status === "open"
                    ? "error"
                    : evt.status === "acknowledged"
                      ? "warning"
                      : "online"
                }
              >
                {evt.status}
              </Badge>
            </td>
            <td className="px-4 py-2 text-xs text-muted-foreground">
              {formatDate(evt.created_at)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
